import React, { useEffect, useRef } from "react";
import { simplifyPath } from '@src/features/zones/utils';
import { ZoneType } from "@src/core/features";
import { selectFreehandShapeUponCreation, setFreehandShapeUponCreation } from '@src/features/zones/redux';
import { useDispatch, useSelector } from "react-redux";
import { ZoneTypeMarkerColor } from '@src/features/zones/config/zones-constants';

interface FreehandShapeDrawerProps {
  map: google.maps.Map | null;
  active?: boolean;
  onComplete?: (path: google.maps.LatLngLiteral[]) => void;
}

const FreehandShapeDrawer: React.FC<FreehandShapeDrawerProps> = ({ map, active, onComplete }) => {
  const dispatch = useDispatch();
  const freehandShapeUponCreation = useSelector(selectFreehandShapeUponCreation);
  const shapeRef = useRef(freehandShapeUponCreation);
  const onCompleteRef = useRef(onComplete);

  shapeRef.current = freehandShapeUponCreation;
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!map || !active) return;
    map.setOptions({ draggableCursor: "crosshair" });

    let isDrawing = false;
    let currentPath: google.maps.LatLngLiteral[] = [];
    let polyline: google.maps.Polyline | null = null;

    const handleMouseDown = (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return;
      isDrawing = true;
      map.setOptions({ draggable: false });

      currentPath = [e.latLng.toJSON()];

      polyline = new google.maps.Polyline({
        map,
        path: currentPath,
        strokeColor: ZoneTypeMarkerColor[shapeRef.current?.type || ZoneType.TEMPORARY],
        strokeOpacity: 1.0,
        strokeWeight: 2,
        clickable: false,
      });
    };

    const handleMouseMove = (e: google.maps.MapMouseEvent) => {
      if (!isDrawing || !polyline || !e.latLng) return;
      currentPath.push(e.latLng.toJSON());
      polyline.setPath(currentPath);
    };

    const handleMouseUp = () => {
      if (!isDrawing) return;
      isDrawing = false;
      map.setOptions({ draggable: true });

      if (!polyline || currentPath.length < 3) {
        polyline?.setMap(null);
        return;
      }

      const simplified = simplifyPath(currentPath);

      polyline.setMap(null);

      dispatch(setFreehandShapeUponCreation({
        id: shapeRef.current?.id || 0,
        name: shapeRef.current?.name || "",
        type: shapeRef.current?.type || ZoneType.TEMPORARY,
        polygonCoordinates: simplified,
      }));

      onCompleteRef.current?.(simplified);
    };

    const down = map.addListener("mousedown", handleMouseDown);
    const move = map.addListener("mousemove", handleMouseMove);
    const up = map.addListener("mouseup", handleMouseUp);

    return () => {
      google.maps.event.removeListener(down);
      google.maps.event.removeListener(move);
      google.maps.event.removeListener(up);
      map.setOptions({ draggable: true });
      map.setOptions({ draggableCursor: "default" });
    };
  }, [map, active, dispatch]);

  return null;
};

export default FreehandShapeDrawer;
